import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/styles'
import { Box, Card, CardContent, Typography } from '@material-ui/core'
import Page from 'src/components/Page'

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4, 2, 5),
    backgroundColor: '#F8FAFC'
  },
  card: {
    width: '100%',
    maxWidth: 620,
    border: '1px solid #E2E7EB',
    borderRadius: 16,
    boxShadow: '0 10px 30px rgba(31, 48, 61, 0.08)',
    overflow: 'hidden',
    position: 'relative',
    '&::before': {
      position: 'absolute',
      top: 0,
      right: 0,
      left: 0,
      height: 3,
      content: "''",
      background: 'linear-gradient(90deg, #DF2935, #B72556, #54208B)'
    }
  },
  content: {
    padding: theme.spacing(5, 4),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 2.5)
    }
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 42,
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  logo: {
    display: 'block',
    width: 184,
    height: 'auto',
    [theme.breakpoints.down('xs')]: {
      width: 155
    }
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1)
  },
  heading: {
    marginBottom: theme.spacing(1),
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.2
  },
  description: {
    marginBottom: theme.spacing(2.5),
    fontSize: 12,
    lineHeight: 1.45
  },
  body: {
    '& .MuiButton-containedPrimary': {
      minHeight: 46,
      borderRadius: 8,
      boxShadow: '0 4px 10px rgba(191, 30, 45, 0.14)',
      fontWeight: 700,
      textTransform: 'none'
    },
    '& .MuiButton-containedPrimary.Mui-disabled': {
      backgroundColor: '#DEDEDE',
      color: '#9CA3A8',
      boxShadow: 'none'
    },
    '& .MuiButton-outlinedPrimary': {
      borderRadius: 8,
      textTransform: 'none'
    },
    '& .MuiOutlinedInput-root': {
      minHeight: 52,
      borderRadius: 8
    },
    '& .MuiOutlinedInput-input': {
      padding: '15px 14px'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.secondary
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main
    },
    '& .MuiOutlinedInput-root.Mui-focused': {
      boxShadow: '0 0 0 2px rgba(207, 38, 56, 0.08)'
    },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.error.main
    }
  },
  footer: {
    marginTop: theme.spacing(2.5),
    textAlign: 'center'
  }
}))

function AuthPageShell ({ title, heading, description, actions, children, copyright }) {
  const classes = useStyles()

  return (
    <Page className={classes.root} title={title}>
      <Card className={classes.card} elevation={0}>
        <CardContent className={classes.content}>
          <Box className={classes.topRow}>
            <img className={classes.logo} src='/images/logo_2024.png' alt='PlusAdvance' />
            {actions ? <Box className={classes.actions}>{actions}</Box> : null}
          </Box>
          <Typography className={classes.heading} component='h1' variant='h4'>
            {heading}
          </Typography>
          {description ? (
            <Typography className={classes.description} color='textSecondary' component='div' variant='body2'>
              {description}
            </Typography>
          ) : null}
          <Box className={classes.body}>{children}</Box>
        </CardContent>
      </Card>
      {copyright ? (
        <Typography className={classes.footer} color='textSecondary' variant='caption'>
          {copyright}
        </Typography>
      ) : null}
    </Page>
  )
}

AuthPageShell.propTypes = {
  title: PropTypes.string.isRequired,
  heading: PropTypes.node.isRequired,
  description: PropTypes.node,
  actions: PropTypes.node,
  children: PropTypes.node.isRequired,
  copyright: PropTypes.node
}

export default AuthPageShell
